// saveQueue — the order the editor saves in, pure: (state, event) → (state,
// effects). The impure runner is `useSaveQueue.ts`; it owns the timer, the
// page listeners and the port calls, and feeds their outcomes back here.
//
// Why it exists (the real-time editor plan, PR1): every content edit is
// guarded by the material's whole-content hash, so two saves sent at once
// race and the second is refused as stale (typing in block 1, then block 2,
// lost block 2's text). Here each material has ONE lane: one save in flight
// at a time, the rest waiting in order, and each sent with the hash the
// previous one returned.
//
// Text the author is still typing is a DRAFT: it waits until the author
// pauses (`IDLE_SAVE_MS`) or leaves the field, the page is hidden or closed,
// or another edit to the material is committed (which sends every earlier
// draft first, so the order holds). Per field only the latest text is kept,
// and idle saves are spaced at least `MIN_IDLE_GAP_MS` apart, so a fast typist
// stays well inside the server's command budget (60 per minute per user).
//
// A field save whose element has no path yet (a block whose insert is still
// waiting or in flight) simply waits behind that insert: by the time it is
// sent the insert has landed and threaded the new path in. A field whose
// path never arrives (its insert was refused, or the content has none) is
// dropped when its turn comes, as an unsavable edit always was.
//
// A refused field save goes back to the drafts (the author's text is never
// rolled back) and goes out again with the next flush. A refused structural
// edit is the runner's to undo; this queue just moves on.
//
// A block's text is saved against its BASE, the text the author started
// from (PR3): the server keeps the save to that one block, so a change
// elsewhere in the material (the assistant's, say) no longer refuses it. The
// base is pinned when the field's first edit comes in and kept while its
// edits are on their way; a save that lands moves it on to the saved text.
// If the block itself changed elsewhere, the save is refused with the text
// there now: it waits as a CONFLICT (never sent, the lane not quiet) until
// the author picks. Keep mine sends it again on the new text; take theirs
// drops it and shows theirs.

export type { FieldSave, Lane, Paths, SaveEffect, SaveEvent, SaveQueue };
export {
    emptySaveQueue,
    fieldValue,
    hasDraft,
    IDLE_SAVE_MS,
    isEditable,
    isQuiet,
    laneOf,
    MIN_IDLE_GAP_MS,
    stepSaveQueue,
};

import { exhaustSwitchCase } from "@bilda/utils";
import type { ElementId } from "../Editor/types.js";

// A pause this long while typing saves the draft.
const IDLE_SAVE_MS = 1500;
// Idle saves are at least this far apart: at most 30 a minute, half the
// server's per-user command budget, the rest left for everything else.
const MIN_IDLE_GAP_MS = 2000;

type Paths = ReadonlyMap<ElementId, readonly string[]>;

// One field's text (a block's primary text) or a choice's feedback. Only the
// latest value per field is ever kept or sent.
type FieldSave =
    | { readonly type: "text"; readonly id: ElementId; readonly text: string }
    | {
          readonly type: "feedback";
          readonly id: ElementId;
          readonly feedback: string;
      };

// Any other edit (insert, move, remove, retype, attrs): `run` is the runner's
// own payload, opaque here. `mints` names the element it creates, whose path
// its success threads in; `within`, the elements made inside it (a new
// question's answers), each at that path and its own id.
type Save<R> =
    | FieldSave
    | {
          readonly type: "structural";
          readonly run: R;
          readonly mints: ElementId | undefined;
          readonly within?: readonly ElementId[];
      };

// A block's text save the server refused because the block changed
// elsewhere: the author's text and the text there now.
type Conflict = {
    readonly id: ElementId;
    readonly text: string;
    readonly current: string;
};

type Lane<R> = {
    // The hash the next save is sent with.
    readonly hash: string;
    readonly paths: Paths;
    // Per block being edited: the text its edits build on (see the header).
    readonly bases: ReadonlyMap<ElementId, string>;
    // Refused saves waiting for the author's pick.
    readonly conflicts: readonly Conflict[];
    // Typed but not yet due.
    readonly drafts: readonly FieldSave[];
    // Due, in order.
    readonly queue: readonly Save<R>[];
    readonly inFlight: Save<R> | undefined;
};

type SaveQueue<R> = {
    readonly lanes: ReadonlyMap<string, Lane<R>>;
    // When the last idle save was made due (spacing; absent: none yet).
    readonly lastIdleSaveAt: number | undefined;
};

type SaveEvent<R> =
    // The material's content was read: its hash and paths (taken only while
    // the lane is quiet; a read that lands mid-save may predate it).
    | {
          readonly type: "loaded";
          readonly materialId: string;
          readonly hash: string;
          readonly paths: Paths;
      }
    // The author typed: the field's latest text, not yet due. `shown` is
    // what the field showed before the edit (a text field's base when its
    // first edit comes in).
    | {
          readonly type: "drafted";
          readonly materialId: string;
          readonly save: FieldSave;
          readonly shown?: string;
      }
    // An edit is done (blur, Enter, a structural edit): every draft of the
    // material, then this, become due.
    | {
          readonly type: "committed";
          readonly materialId: string;
          readonly save: Save<R>;
          readonly shown?: string;
      }
    // The author paused. `held` is a block whose "/" menu or Ask line is in
    // progress: its draft waits.
    | {
          readonly type: "idle-reached";
          readonly now: number;
          readonly held: ElementId | undefined;
      }
    // The page is being hidden or closed: everything goes now.
    | { readonly type: "page-left" }
    | {
          readonly type: "saved";
          readonly materialId: string;
          readonly hash: string;
          // The minted element's path (a structural save that created one).
          readonly path: readonly string[] | undefined;
      }
    | { readonly type: "save-failed"; readonly materialId: string }
    // The text save in flight was refused: its block reads `current` now.
    | {
          readonly type: "save-conflicted";
          readonly materialId: string;
          readonly current: string;
      }
    // The author's pick on a conflict: save theirs over (on the new text),
    // or drop the draft.
    | {
          readonly type: "kept-mine";
          readonly materialId: string;
          readonly id: ElementId;
      }
    | {
          readonly type: "took-theirs";
          readonly materialId: string;
          readonly id: ElementId;
      };

type SaveEffect<R> =
    | {
          readonly type: "send-text";
          readonly materialId: string;
          readonly path: readonly string[];
          readonly text: string;
          readonly hash: string;
          // The text it builds on (absent: whole-material guard only).
          readonly base: string | undefined;
      }
    | {
          readonly type: "send-feedback";
          readonly materialId: string;
          readonly path: readonly string[];
          readonly feedback: string;
          readonly hash: string;
      }
    | {
          readonly type: "send-structural";
          readonly materialId: string;
          readonly run: R;
          readonly hash: string;
          readonly paths: Paths;
      }
    // (Re)start the idle timer.
    | { readonly type: "arm-idle"; readonly ms: number }
    // A block's save waits for the author's pick (offer the choice).
    | {
          readonly type: "offer-choice";
          readonly materialId: string;
          readonly id: ElementId;
      }
    // Theirs was taken: show `text` in the block.
    | {
          readonly type: "show-text";
          readonly materialId: string;
          readonly id: ElementId;
          readonly text: string;
      };

type Step<R> = {
    readonly state: SaveQueue<R>;
    readonly effects: readonly SaveEffect<R>[];
};

const emptySaveQueue: SaveQueue<never> = {
    lanes: new Map(),
    lastIdleSaveAt: undefined,
};

const emptyLane: Lane<never> = {
    hash: "",
    paths: new Map(),
    bases: new Map(),
    conflicts: [],
    drafts: [],
    queue: [],
    inFlight: undefined,
};

const laneOf = <R>(q: SaveQueue<R>, materialId: string): Lane<R> =>
    q.lanes.get(materialId) ?? emptyLane;

const sameField = (a: Save<unknown>, b: FieldSave): boolean =>
    a.type === b.type && a.id === b.id;

// Whether an edit to `id` can be saved: it has a path, or the insert that
// makes it is still waiting or in flight.
const isEditable = <R>(lane: Lane<R>, id: ElementId): boolean =>
    lane.paths.has(id) ||
    [lane.inFlight, ...lane.queue].some(
        (s) =>
            s?.type === "structural" &&
            (s.mints === id || (s.within ?? []).includes(id)),
    );

// Nothing on its way or waiting for the author's pick (drafts may still
// wait).
const isQuiet = <R>(lane: Lane<R>): boolean =>
    lane.inFlight === undefined &&
    lane.queue.length === 0 &&
    lane.conflicts.length === 0;

// Whether the field has typed text waiting (a refused save waits here too).
const hasDraft = <R>(lane: Lane<R>, field: FieldSave): boolean =>
    lane.drafts.some((d) => sameField(d, field));

const fieldValue = (field: FieldSave): string =>
    field.type === "text" ? field.text : field.feedback;

// Make `save` due. A field already waiting takes the new text in its place
// (the in-flight one is already sent and stays as it is).
const enqueue = <R>(queue: readonly Save<R>[], save: Save<R>) => {
    if (save.type === "structural") return [...queue, save];
    const at = queue.findIndex((s) => sameField(s, save));
    return at === -1
        ? [...queue, save]
        : queue.map((s, i) => (i === at ? save : s));
};

const withoutDraft = (drafts: readonly FieldSave[], save: FieldSave) =>
    drafts.filter((d) => !sameField(d, save));

const conflictOf = <R>(lane: Lane<R>, save: Save<R>): Conflict | undefined =>
    save.type === "text"
        ? lane.conflicts.find((c) => c.id === save.id)
        : undefined;

// The conflict now holding `text` instead (the author kept typing in it).
const withConflictText = <R>(
    lane: Lane<R>,
    id: ElementId,
    text: string,
): Lane<R> => ({
    ...lane,
    conflicts: lane.conflicts.map((c) => (c.id === id ? { ...c, text } : c)),
});

// Pin a text field's base on its first edit (kept while one is on its way).
const pinBase = <R>(
    lane: Lane<R>,
    save: Save<R>,
    shown: string | undefined,
): Lane<R> =>
    save.type !== "text" || shown === undefined || lane.bases.has(save.id)
        ? lane
        : { ...lane, bases: new Map([...lane.bases, [save.id, shown]]) };

// Whether an edit of the field is still on its way (drafted, due or sent).
const pending = <R>(lane: Lane<R>, field: FieldSave): boolean =>
    [...lane.drafts, ...lane.queue, lane.inFlight].some(
        (s) => s !== undefined && sameField(s, field),
    );

const withoutBase = <R>(lane: Lane<R>, id: ElementId): Lane<R> => ({
    ...lane,
    bases: new Map([...lane.bases].filter(([k]) => k !== id)),
});

// Move drafts (all, or all but those of `held`) into the queue.
const promote = <R>(lane: Lane<R>, held?: ElementId): Lane<R> => {
    const due = lane.drafts.filter((d) => d.id !== held);
    return {
        ...lane,
        drafts: lane.drafts.filter((d) => d.id === held),
        queue: due.reduce(enqueue<R>, lane.queue),
    };
};

// Send the next due save, if the lane is free. Field saves with no path are
// dropped (see the header).
const pump = <R>(
    materialId: string,
    lane: Lane<R>,
): { readonly lane: Lane<R>; readonly effects: readonly SaveEffect<R>[] } => {
    if (lane.inFlight !== undefined) return { lane, effects: [] };
    const [head, ...rest] = lane.queue;
    if (head === undefined) return { lane, effects: [] };
    const sending = { ...lane, queue: rest, inFlight: head };
    switch (head.type) {
        case "structural":
            return {
                lane: sending,
                effects: [
                    {
                        type: "send-structural",
                        materialId,
                        run: head.run,
                        hash: lane.hash,
                        paths: lane.paths,
                    },
                ],
            };
        case "text":
        case "feedback": {
            const path = lane.paths.get(head.id);
            if (path === undefined)
                return pump(materialId, { ...lane, queue: rest });
            return {
                lane: sending,
                effects: [
                    head.type === "text"
                        ? {
                              type: "send-text",
                              materialId,
                              path,
                              text: head.text,
                              hash: lane.hash,
                              base: lane.bases.get(head.id),
                          }
                        : {
                              type: "send-feedback",
                              materialId,
                              path,
                              feedback: head.feedback,
                              hash: lane.hash,
                          },
                ],
            };
        }
        default:
            return exhaustSwitchCase(head);
    }
};

const withLane = <R>(
    q: SaveQueue<R>,
    materialId: string,
    lane: Lane<R>,
): SaveQueue<R> => ({
    ...q,
    lanes: new Map([...q.lanes, [materialId, lane]]),
});

// Replace one lane and send its next save.
const settle = <R>(
    q: SaveQueue<R>,
    materialId: string,
    lane: Lane<R>,
    effects: readonly SaveEffect<R>[] = [],
): Step<R> => {
    const pumped = pump(materialId, lane);
    return {
        state: withLane(q, materialId, pumped.lane),
        effects: [...effects, ...pumped.effects],
    };
};

// Promote drafts in every lane and send what is due.
const promoteAll = <R>(
    q: SaveQueue<R>,
    held: ElementId | undefined,
    lastIdleSaveAt: number | undefined,
): Step<R> =>
    [...q.lanes].reduce<Step<R>>(
        (step, [materialId, lane]) => {
            const next = settle(step.state, materialId, promote(lane, held));
            return {
                state: next.state,
                effects: [...step.effects, ...next.effects],
            };
        },
        { state: { ...q, lastIdleSaveAt }, effects: [] },
    );

// O(L + D + Q) per event in the material count, a lane's drafts and its
// queue; all three stay small (drafts are per field, the queue coalesces).
const stepSaveQueue = <R>(q: SaveQueue<R>, event: SaveEvent<R>): Step<R> => {
    switch (event.type) {
        case "loaded": {
            const lane = laneOf(q, event.materialId);
            if (!isQuiet(lane)) return { state: q, effects: [] };
            return {
                state: withLane(q, event.materialId, {
                    ...lane,
                    hash: event.hash,
                    paths: event.paths,
                }),
                effects: [],
            };
        }
        case "drafted": {
            const lane = laneOf(q, event.materialId);
            // Typing on in a block whose save waits for the pick: the pick
            // will carry the latest text.
            if (conflictOf(lane, event.save) !== undefined)
                return {
                    state: withLane(
                        q,
                        event.materialId,
                        withConflictText(
                            lane,
                            event.save.id,
                            fieldValue(event.save),
                        ),
                    ),
                    effects: [],
                };
            const pinned = pinBase(lane, event.save, event.shown);
            return {
                state: withLane(q, event.materialId, {
                    ...pinned,
                    drafts: [
                        ...withoutDraft(pinned.drafts, event.save),
                        event.save,
                    ],
                }),
                effects: [{ type: "arm-idle", ms: IDLE_SAVE_MS }],
            };
        }
        case "committed": {
            const lane = laneOf(q, event.materialId);
            const { save } = event;
            if (save.type === "text" && conflictOf(lane, save) !== undefined) {
                const held = withConflictText(lane, save.id, save.text);
                return settle(q, event.materialId, promote(held), [
                    {
                        type: "offer-choice",
                        materialId: event.materialId,
                        id: save.id,
                    },
                ]);
            }
            const pinned = pinBase(lane, save, event.shown);
            const drafts =
                save.type === "structural"
                    ? pinned.drafts
                    : withoutDraft(pinned.drafts, save);
            const promoted = promote({ ...pinned, drafts });
            return settle(q, event.materialId, {
                ...promoted,
                queue: enqueue(promoted.queue, save),
            });
        }
        case "idle-reached": {
            const waiting = [...q.lanes.values()].some((l) =>
                l.drafts.some((d) => d.id !== event.held),
            );
            if (!waiting) return { state: q, effects: [] };
            const since =
                q.lastIdleSaveAt === undefined
                    ? Number.POSITIVE_INFINITY
                    : event.now - q.lastIdleSaveAt;
            if (since < MIN_IDLE_GAP_MS)
                return {
                    state: q,
                    effects: [
                        { type: "arm-idle", ms: MIN_IDLE_GAP_MS - since },
                    ],
                };
            return promoteAll(q, event.held, event.now);
        }
        case "page-left":
            return promoteAll(q, undefined, q.lastIdleSaveAt);
        case "saved": {
            const lane = laneOf(q, event.materialId);
            const sent = lane.inFlight;
            const minted =
                sent?.type === "structural" &&
                sent.mints !== undefined &&
                event.path !== undefined
                    ? new Map([
                          ...lane.paths,
                          [sent.mints, event.path],
                          ...(sent.within ?? []).map(
                              (id): [ElementId, readonly string[]] => [
                                  id,
                                  [...(event.path ?? []), id],
                              ],
                          ),
                      ])
                    : lane.paths;
            const landed = {
                ...lane,
                hash: event.hash,
                paths: minted,
                inFlight: undefined,
            };
            // A saved text is the base of the field's next edit on its way;
            // with none, the next edit pins what the field shows then.
            const based =
                sent?.type !== "text"
                    ? landed
                    : pending(landed, sent)
                      ? {
                            ...landed,
                            bases: new Map([
                                ...landed.bases,
                                [sent.id, sent.text],
                            ]),
                        }
                      : withoutBase(landed, sent.id);
            return settle(q, event.materialId, based);
        }
        case "save-conflicted": {
            const lane = laneOf(q, event.materialId);
            const sent = lane.inFlight;
            if (sent?.type !== "text")
                return stepSaveQueue(q, {
                    type: "save-failed",
                    materialId: event.materialId,
                });
            // The newest text of the block waits with the conflict; none of
            // its edits goes out until the author picks.
            const newest =
                [...lane.queue, ...lane.drafts]
                    .flatMap((s) =>
                        s.type !== "structural" && sameField(s, sent)
                            ? [s]
                            : [],
                    )
                    .at(-1) ?? sent;
            return settle(
                q,
                event.materialId,
                {
                    ...lane,
                    drafts: withoutDraft(lane.drafts, sent),
                    queue: lane.queue.filter(
                        (s) => s.type === "structural" || !sameField(s, sent),
                    ),
                    inFlight: undefined,
                    conflicts: [
                        ...lane.conflicts,
                        {
                            id: sent.id,
                            text: fieldValue(newest),
                            current: event.current,
                        },
                    ],
                },
                [
                    {
                        type: "offer-choice",
                        materialId: event.materialId,
                        id: sent.id,
                    },
                ],
            );
        }
        case "kept-mine": {
            const lane = laneOf(q, event.materialId);
            const c = lane.conflicts.find((x) => x.id === event.id);
            if (c === undefined) return { state: q, effects: [] };
            const mine: FieldSave = { type: "text", id: c.id, text: c.text };
            return settle(q, event.materialId, {
                ...lane,
                conflicts: lane.conflicts.filter((x) => x.id !== c.id),
                bases: new Map([...lane.bases, [c.id, c.current]]),
                queue: enqueue(lane.queue, mine),
            });
        }
        case "took-theirs": {
            const lane = laneOf(q, event.materialId);
            const c = lane.conflicts.find((x) => x.id === event.id);
            if (c === undefined) return { state: q, effects: [] };
            return settle(
                q,
                event.materialId,
                withoutBase(
                    {
                        ...lane,
                        conflicts: lane.conflicts.filter((x) => x.id !== c.id),
                    },
                    c.id,
                ),
                [
                    {
                        type: "show-text",
                        materialId: event.materialId,
                        id: c.id,
                        text: c.current,
                    },
                ],
            );
        }
        case "save-failed": {
            const lane = laneOf(q, event.materialId);
            const sent = lane.inFlight;
            // The refused text waits as a draft again, unless newer text for
            // the same field is already waiting.
            const back =
                sent === undefined || sent.type === "structural"
                    ? undefined
                    : sent;
            const keep =
                back !== undefined &&
                ![...lane.drafts, ...lane.queue].some((s) =>
                    sameField(s, back),
                );
            return settle(q, event.materialId, {
                ...lane,
                drafts:
                    keep && back !== undefined
                        ? [...lane.drafts, back]
                        : lane.drafts,
                inFlight: undefined,
            });
        }
        default:
            return exhaustSwitchCase(event);
    }
};
